import { Flex, useMantineTheme, Loader, Center } from '@mantine/core';
import { IconChevronLeft } from '@tabler/icons-react';
import useLang from 'lang';
import SimpleLayout from 'layouts/SimpleLayout';
import { useMemo, useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MYDOCUMENTS_PAGE_PATH } from 'routes/paths';
import P2Medium from 'theme/components/Typography/P2Medium';

/**
 * Detail of a document.
 */
export default function DocumentDetail() {
  const theme = useMantineTheme();
  const { _t } = useLang();
  const { documentId } = useParams();
  const [isPdf, setIsPdf] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const documentUrl = useMemo(() => `https://admin.phillip.city/api/v1/help-desk/document/${documentId}`, [documentId]);

  const iframeUrl = useMemo(() => `${documentUrl}#toolbar=0&navpanes=0&view=Fit&zoom=Fit`, [documentUrl]);

  const googleViewerUrl = `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(documentUrl)}`;

  // Check if the document is a PDF
  useEffect(() => {
    /**
     * Fetches the document url and checks if it is a PDF.
     *
     * Sets isLoading to true while checking the document type.
     * If the document is a PDF, sets isPdf to true.
     * If the document is not a PDF, sets isPdf to false.
     * If there is an error, sets error to true.
     * Sets isLoading to false after checking the document type.
     */
    const checkDocumentType = async () => {
      try {
        setLoading(true);
        const response = await fetch(documentUrl, { method: 'HEAD' });
        const contentType = response.headers.get('content-type');
        // console.log('Content-Type:', contentType);
        setIsPdf(contentType?.includes('application/pdf') || false);
        setLoading(false);
      } catch (err) {
        // console.error('Error checking document type:', err);
        setError(true);
        setLoading(false);
      }
    };

    checkDocumentType();
  }, [documentUrl]);

  return (
    <SimpleLayout>
      <Flex justify="flex-start" align="center" h={40} px="1rem">
        <Link
          to={MYDOCUMENTS_PAGE_PATH.original}
          style={{ textDecoration: 'none', display: 'flex', alignContent: 'center' }}
        >
          <IconChevronLeft size="1.5rem" color={theme.colors?.gray?.[9]} />
          <P2Medium color={theme.colors?.gray?.[9]}>{_t('Back')}</P2Medium>
        </Link>
      </Flex>
      {loading ? (
        <Center style={{ height: 'calc(100vh - 125px)' }}>
          <Loader size="lg" />
        </Center>
      ) : error ? (
        <Center style={{ height: 'calc(100vh - 125px)' }}>
          <P2Medium>{_t('Error loading document')}</P2Medium>
        </Center>
      ) : isPdf ? (
        <Center
          style={{
            height: 'calc(100vh - 125px)',
            overflow: 'auto',
            maxWidth: '90%',
            margin: '0 auto',
            display: 'block',
          }}
        >
          <iframe
            title={_t('Document')}
            src={googleViewerUrl}
            width="100%"
            style={{ height: 'calc(100vh - 125px)', maxWidth: '90%', margin: '0 auto', display: 'block' }}
          ></iframe>
        </Center>
      ) : (
        <iframe
          title={_t('Document')}
          src={iframeUrl}
          width="100%"
          style={{ height: 'calc(100vh - 125px)', maxWidth: '90%', margin: '0 auto', display: 'block' }}
        ></iframe>
      )}
    </SimpleLayout>
  );
}
